import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {Kunde} from "../../api";
import DataTable from "../data/DataTable";
import useCustomFilters from "../data/useCustomFilters";

export type KundenFilter = {
    search?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<KundenFilter>, "filter"> &
    Partial<Pick<DataRequest<KundenFilter>, "filter">>;

const tableName: string = "kunden"

export default function KundenDataTable({...input}: Props) {
    const request = useDataRequest<KundenFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp request={request} filteredStateFilter={filteredState}/>
            <KundenResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<KundenFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<KundenFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-kunde");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-kunde");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function KundenResults({
                           request,
                           setFilteredState,
                       }: {
    request: DataRequestState<KundenFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<KundenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadKunden: DataLoader<KundenFilter, Kunde> = useCallback(
        async (params) =>
            (await getApi()).kunden.sucheKunden({
                ...params,
            }),
        [getApi]
    );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadKunden}
            onRowClick={(row) => {
                navigate("/kunden/" + row.id);
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Kunde>> {
    const {t: translation} = useT("customer");

    return useMemo(
        () => [
            {
                field: "name",
                headerName: capitalize(translation("name")),
                flex: 1,
            },
            {
                field: "strasse",
                headerName: capitalize(translation("street")),
                flex: 1,
            },
            {
                field: "plz",
                headerName: capitalize(translation("plz")),
                flex: 1,
            },
            {
                field: "ort",
                headerName: capitalize(translation("city")),
                flex: 1,
            },
            {
                field: "telefon",
                headerName: capitalize(translation("phone")),
                flex: 1,
            },
            {
                field: "email",
                headerName: capitalize(translation("email")),
                flex: 1,
            },
        ],
        [translation]
    );
}
